import deepEqual from 'fast-deep-equal'
import { toJS } from 'mobx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { TableStore } from '../../../components/table2/table_store'
import { useUser } from '../../../hooks/useUser'
import { transformSelectComponentFilters } from '../../../util/transformSelectComponentFilters'

export interface Params {
    [key: string]: any
}

export interface PaginatedResponseMeta {
    current_page: number
    last_page: number
    from: number
    to: number
    total: number
}

export interface PaginatedResponse<T> {
    data: {
        data: T[]
        totals?: any
        meta?: PaginatedResponseMeta
        current_page?: number
        last_page?: number
        from?: number
        to?: number
        total?: number
    }
}

export interface TableHooks<T> {
    totals: any
    loading: boolean
    items: T[]
    params: Params
    pagination: InnerPagination
    commit: boolean
    setParams: (params: Params) => void
    reload: () => void
    rerender: () => void
    setPage: (page: number) => void
    setQuery: (query: string) => void
    setFilter: (filter: any) => void
    mutateItems: (callback: (items: T[]) => T[]) => void
    setCommit: (commit: boolean) => void
}

export type LoadFunc<T> = (params: Params) => Promise<PaginatedResponse<T>>
export type RequestContextKey = string | number | boolean | null | undefined

const emptyPagination = (): InnerPagination => ({
    current: null,
    last_page: null,
    from: null,
    to: null,
    total: null,
})

export const useTableHooks = <T>(
    load: LoadFunc<T>,
    defaultParams: Params = {},
    manualCommit: boolean = false,
): TableHooks<T> => {
    if (!load) {
        console.error('No load function passed to useTableHooks')
        throw new Error('No load function passed to useTableHooks')
    }

    const [commit, setCommit] = useState(!manualCommit)
    const { profile, searchParams } = useUser()
    const [totals, setTotals] = useState(null)
    const [items, setItems] = useState<T[]>([])
    const [loading, setLoading] = useState(false)
    const [params, setParams] = useState<Params>({
        ...defaultParams,
        query: undefined,
        perPage: defaultParams?.perPage || 15,
        page: 1,
    })
    const [pagination, setPagination] = useState<InnerPagination>(emptyPagination)
    const latestRequest = useRef(0)

    const paramsJsonString = useMemo(() => JSON.stringify(params), [params])

    const prepareLoad = (params: any) => {
        const request = ++latestRequest.current
        setLoading(true)
        const clearCurrentData = () => {
            setItems([])
            setTotals(null)
            setPagination(emptyPagination())
        }
        let loadPromise: Promise<PaginatedResponse<T> | undefined>
        try {
            const paramsCopy = transformSelectComponentFilters(params)
            loadPromise = Promise.resolve(load(paramsCopy))
        } catch (error) {
            loadPromise = Promise.reject(error)
        }
        loadPromise
            .then((res) => {
                if (request !== latestRequest.current) {
                    return
                }

                if (!res) {
                    clearCurrentData()
                    return
                }

                setItems(res.data.data)
                const nextTotals = res.data.totals ?? null
                if (!deepEqual(nextTotals, totals)) {
                    setTotals(nextTotals)
                }
                if (res.data.meta) {
                    setPagination({
                        current: res.data.meta.current_page,
                        last_page: res.data.meta.last_page,
                        from: res.data.meta.from,
                        to: res.data.meta.to,
                        total: res.data.meta.total,
                    })
                } else {
                    setPagination({
                        current: res.data.current_page ?? null,
                        last_page: res.data.last_page ?? null,
                        from: res.data.from ?? null,
                        to: res.data.to ?? null,
                        total: res.data.total ?? null,
                    })
                }
            })
            .catch((error) => {
                if (request === latestRequest.current) {
                    clearCurrentData()
                    console.error('Failed to load table data', error)
                }
            })
            .finally(() => {
                if (request === latestRequest.current) {
                    setLoading(false)
                }
            })
    }

    useEffect(
        () => () => {
            latestRequest.current++
        },
        [],
    )

    useEffect(() => {
        if (commit) {
            prepareLoad(params)
        }
    }, [profile, paramsJsonString, commit, searchParams])

    const rerender = () => setItems([...items])
    const reload = () => {
        if (commit) {
            prepareLoad(params)
        }
    }
    const setPage = (page: number) => setParams({ ...params, page })
    const setQuery = (query: string) => setParams({ ...params, query, page: 1 })
    const setFilter = (filter: any) => setParams({ ...params, filter, page: 1 })

    /**
     * Allows items to be mutated. Callback is passed the entire
     * item list and is expected to return a mutated list.
     *
     * @param callback
     */
    const mutateItems = useCallback((callback) => setItems((items) => callback(items)), [])

    return {
        totals,
        loading,
        items,
        params,
        pagination,
        commit,
        setParams,
        reload,
        rerender,
        setPage,
        setQuery,
        setFilter,
        mutateItems,
        setCommit,
    }
}

export interface TableHooks<T> {
    totals: any
    loading: boolean
    items: T[]
    params: Params
    pagination: InnerPagination
    commit: boolean
    setParams: (params: Params) => void
    reload: () => void
    rerender: () => void
    setPage: (page: number) => void
    setQuery: (query: string) => void
    setFilter: (filter: any) => void
    mutateItems: (callback: (items: T[]) => T[]) => void
    setCommit: (commit: boolean) => void
}

export interface InnerPagination {
    current: number | null
    last_page: number | null
    from: number | null
    to: number | null
    total: number | null
}

export const useTableHooksFromStore = <T>(
    load: LoadFunc<T>,
    _defaultParams: Params = {},
    manualCommit: boolean = false,
    store: TableStore<T>,
    requestContext?: RequestContextKey,
    options: { requireProfile?: boolean } = {},
): TableHooks<T> => {
    if (!load) {
        console.error('No load function passed to useTableHooks')
        throw new Error('No load function passed to useTableHooks')
    }

    const { profile, searchParams } = useUser()
    const [commit, setCommit] = useState(!manualCommit)
    const latestRequest = useRef(0)

    const prepareLoad = (params: any) => {
        const request = ++latestRequest.current
        store.setLoading(true)
        const clearCurrentData = () => {
            store.setItems([])
            store.setTotals(null)
            store.setPagination(emptyPagination())
        }
        let loadPromise: Promise<PaginatedResponse<T> | undefined>
        try {
            const paramsCopy = transformSelectComponentFilters(toJS(params))

            // Remove any `period` attributes from a "last N days/months" type filter
            if (paramsCopy.filter) {
                Object.keys(paramsCopy.filter).forEach((k) => {
                    if (paramsCopy.filter[k]?.last) {
                        delete paramsCopy.filter[k].period
                    }
                })
            }

            loadPromise = Promise.resolve(load(paramsCopy))
        } catch (error) {
            loadPromise = Promise.reject(error)
        }
        loadPromise
            .then((res) => {
                if (request !== latestRequest.current) {
                    return
                }

                if (!res) {
                    clearCurrentData()
                    return
                }

                store.setItems(res.data.data)
                const nextTotals = res.data.totals ?? null
                if (!deepEqual(nextTotals, store.totals)) {
                    store.setTotals(nextTotals)
                }
                if (res.data.meta) {
                    store.setPagination({
                        current: res.data.meta.current_page,
                        last_page: res.data.meta.last_page,
                        from: res.data.meta.from,
                        to: res.data.meta.to,
                        total: res.data.meta.total,
                    })
                } else {
                    store.setPagination({
                        current: res.data.current_page ?? null,
                        last_page: res.data.last_page ?? null,
                        from: res.data.from ?? null,
                        to: res.data.to ?? null,
                        total: res.data.total ?? null,
                    })
                }
            })
            .catch((error) => {
                if (request === latestRequest.current) {
                    clearCurrentData()
                    console.error('Failed to load table data', error)
                }
            })
            .finally(() => {
                if (request === latestRequest.current) {
                    store.setLoading(false)
                }
            })
    }

    useEffect(
        () => () => {
            latestRequest.current++
        },
        [],
    )

    useEffect(() => {
        if (options.requireProfile !== false && !profile) {
            latestRequest.current++
            store.setItems([])
            store.setTotals(null)
            store.setPagination(emptyPagination())
            store.setLoading(true)
            return
        }
        prepareLoad(store.params)
    }, [profile, JSON.stringify(store.params), requestContext, searchParams, store])

    const rerender = () => {} // @todo does this need to do anything?
    const reload = () => {
        console.log('reloading')
        console.log('inside reload')
        if (options.requireProfile !== false && !profile) {
            latestRequest.current++
            store.setLoading(true)
            return
        }
        prepareLoad(store.params)
    }
    const setPage = (page: number) => {
        if (store.params.page !== page) {
            store.params.page = page
        }
    }
    const setQuery = (query: string) => {
        if (store.params.query !== query) {
            store.params.query = query
            store.params.page = 1
        }
    }
    const setFilter = (filter: any) => {
        if (!deepEqual(store.params.filter, filter)) {
            store.params.filter = filter
            store.params.page = 1
        }
    }

    /**
     * Allows items to be mutated. Callback is passed the entire
     * item list and is expected to return a mutated list.
     *
     * @param {function} callback - Function that takes the current items and returns the mutated items
     * @return {void}
     */
    const mutateItems = (callback) => store.setItems((items) => callback(items))

    return {
        totals: store.totals,
        loading: store.loading,
        items: store.items,
        params: store.params,
        pagination: store.pagination,
        commit,
        setParams: store.setParams,
        reload,
        rerender,
        setPage,
        setQuery,
        setFilter,
        mutateItems,
        setCommit,
    }
}
